import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { GridColumn } from '@progress/kendo-react-grid';
import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import {
  ContextMenu,
  MenuItem,
  MenuSelectEvent,
} from '@progress/kendo-react-layout';
import { plusIcon } from "@progress/kendo-svg-icons";

import { useKeyPress } from '../../../../hooks';

import { Button, ListingGrid } from '../../../common';
// import DepartmentDialog from './DepartmentDialog';

import { PositionsListType } from '../../../../data/types';
import { columns } from './model';

import { ButtonWrapper, ListingGridWrapper } from '../PersonnelCodes.styled';
import { positionsService } from '../../../../services/positionsService';
import PositionDialog from './PositionsDialog';

type SelectionType = { field?: string; dataItem: any };

interface PositionProps {
  selectedPosition: PositionsListType | undefined;
  setSelectedPosition: React.Dispatch<
    React.SetStateAction<PositionsListType | undefined>
  >;
}

const Positions: FC<PositionProps> = ({
    selectedPosition,
    setSelectedPosition,
}) => {
  const [originData, setOriginData] = useState<PositionsListType[]>([]);
  const [filterData, setFilterData] = useState<PositionsListType[]>([]);
  // eslint-disable-next-line
  const [dataTemplate, setDataTemplate] = useState<any[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sort, setSort] = useState<SortDescriptor[]>();
  const [showContext, setShowContext] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<SelectionType>();

  const offset = useRef({ left: 0, top: 0 });

  async function fetchData() {
    try {
      const positionsList: any = await positionsService.getPositions();
      setOriginData(positionsList ?? []);
      setFilterData(positionsList ?? []);

      const positionData: any = await positionsService.getPositionData();
      setDataTemplate(positionData)

    } catch (e) { }

    setIsLoading(false);
  }

  useEffect(() => {
    setIsLoading(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContextMenuOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    offset.current = {
      left: e.pageX,
      top: e.pageY,
    };
    setShowContext(true);
  };

  const handleOnSelect = useCallback(
    (e: MenuSelectEvent) => {
      if (selectedItem) {
        const { field, dataItem } = selectedItem;
        switch (e.item.data.action) {
          case 'sortAsc':
            setSort([
              {
                dir: 'asc',
                field: field ?? '',
              },
            ]);
            break;
          case 'sortDesc':
            setSort([
              {
                dir: 'desc',
                field: field ?? '',
              },
            ]);
            break;
          case 'filterIn':
            if (field) {
              setFilterData(
                originData.filter(
                  (data: any) => data[field] === dataItem[field]
                )
              );
            }
            break;
          case 'filterOut':
            if (field) {
              setFilterData(
                originData.filter(
                  (data: any) => data[field] !== dataItem[field]
                )
              );
            }
            break;
          case 'removeFilters':
            setFilterData(originData);
            break;
          default:
        }
      }
      setShowContext(false);
    },
    [selectedItem, originData]
  );

useKeyPress(
    () => {
    },
    () => {
    },
    () => {
    },
    () => {
        setSelectedPosition({ PositionCode: '' });
    }
  );

  return (
    <ListingGridWrapper>
      {isLoading ? (
        <></>
      ) : (
        <>
          <ListingGrid
            rowHeight={24}
            data={sort ? orderBy(filterData, sort) : filterData}
            dataItemKey="PositionCode"
            onRowClick={(e) => {
              setFilterData((prev) =>
                prev.map((item) => ({
                  ...item,
                  selected: item.PositionCode === e.dataItem.PositionCode,
                }))
              );
            }}
            onRowDoubleClick={(e) => {
                setSelectedPosition(e.dataItem);
            }}
            sortable={true}
            sort={sort}
            onSortChange={(e) => {
              setSort(e.sort);
            }}
            onContextMenu={(event) => {
              handleContextMenuOpen(event.syntheticEvent);
              const { dataItem, field } = event;
              setFilterData((prev) =>
                prev.map((item) => ({
                  ...item,
                  selected: item.PositionCode === dataItem.PositionCode,
                }))
              );
              setSelectedItem({ field, dataItem });
            }}
          >
            {columns.map((column, index) => {
              return <GridColumn {...column} key={index} />;
            })}
          </ListingGrid>
          <ButtonWrapper>
            <Button
              onClick={(e) => {
                e.preventDefault();
                setSelectedPosition({ PositionCode: '' });
              }}
              id="create-btn"
              title="Add New"
              svgIcon={plusIcon}
            >
              ADD <u>N</u>EW
            </Button>
          </ButtonWrapper>
        </>
      )}
      <ContextMenu
        show={showContext}
        offset={offset.current}
        onSelect={handleOnSelect}
        onClose={() => setShowContext(false)}
        className="context-menu"
      >
        <MenuItem
          text="Sort Ascending"
          data={{
            action: 'sortAsc',
          }}
        />
        <MenuItem
          text="Sort Descending"
          data={{
            action: 'sortDesc',
          }}
        />
        <MenuItem
          text="Filter In Selected"
          data={{
            action: 'filterIn',
          }}
        />
        <MenuItem
          text="Filter Out Selected"
          data={{
            action: 'filterOut',
          }}
        />
        <MenuItem
          text="Remove All Filters"
          data={{
            action: 'removeFilters',
          }}
        />
      </ContextMenu>
      {selectedPosition && (
        <PositionDialog
          handleClose={(shouldRefreshData?: boolean) => {
            setSelectedPosition(undefined);
            if (shouldRefreshData) {
              fetchData();
            }
          }}
          data={selectedPosition}
          isEdit={selectedPosition?.PositionCode ? true : false}
          dataTemplate={dataTemplate}
        />
      )}
    </ListingGridWrapper>
  );
};

export default Positions;
